import * as React from "react";
import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../../common/Loader";
import MenuBar from "../../../components/MenuBar";
import { ContentCategoryRow } from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { groupCategories, mergeCandidates } from "../categoryGroups";
import { useCategories } from "../useCategories";
import { ContentSectionTabs } from "../ContentSectionTabs";
import { ConfirmModal } from "../ConfirmModal";
import { EmojiModal } from "./EmojiModal";

export const ContentCategoriesPage: React.FC = () => {
  const { categories, nearVariants, loading, reload } = useCategories();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [emojiFor, setEmojiFor] = useState<ContentCategoryRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [newName, setNewName] = useState("");
  /**
   * Was a native `confirm()`. Merging is not undoable from this screen, so it
   * gets the same styled dialog as the module's other irreversible actions.
   */
  const [pendingMerge, setPendingMerge] = useState<{
    sourceId: string;
    targetId: string;
    label: string;
  } | null>(null);

  const groups = useMemo(() => groupCategories(categories), [categories]);

  /**
   * Promoted drives the "Popular" group end users see, so it is listed in rank
   * order. Move up/down send an absolute target rank and the server renumbers
   * the rest, keeping ranks a contiguous 1..n.
   */
  const promoted = useMemo(
    () =>
      categories
        .filter((category) => category.favorite)
        .sort((a, b) => (a.favoriteRank ?? 0) - (b.favoriteRank ?? 0)),
    [categories],
  );

  const inUseCount = useMemo(
    () => categories.filter((category) => category.questionCount > 0).length,
    [categories],
  );

  const candidates = useMemo(
    () => mergeCandidates(categories, nearVariants),
    [categories, nearVariants],
  );

  const patch = async (
    category: ContentCategoryRow,
    changes: Parameters<typeof Content.updateCategory>[0],
  ) => {
    setBusyId(category.id);
    try {
      await Content.updateCategory(changes);
      await reload({ silent: true });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const saveRename = async (category: ContentCategoryRow) => {
    const name = draftName.trim();
    setEditingId(null);
    if (!name || name === category.name) {
      return;
    }
    // The platform rejects a collision inside {contentType, group} with a
    // conflict error rather than silently merging.
    await patch(category, { categoryId: category.id, name });
  };

  const createCategory = async () => {
    if (!newGroup.trim() || !newName.trim()) {
      toast.error("Group and name are both required");
      return;
    }
    try {
      await Content.createCategory({
        group: newGroup.trim(),
        name: newName.trim(),
      });
      toast.success("Category created");
      setNewGroup("");
      setNewName("");
      setShowCreate(false);
      await reload({ silent: true });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const merge = async (sourceId: string, targetId: string) => {
    try {
      const { moved } = await Content.mergeCategories({
        sourceCategoryId: sourceId,
        targetCategoryId: targetId,
      });
      toast.success(`Moved ${moved} question${moved === 1 ? "" : "s"}`);
      await reload({ silent: true });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const renderCategory = (category: ContentCategoryRow) => {
    const busy = busyId === category.id;
    // Hidden categories serve nobody, so editing them is pointless until they
    // are unhidden. Everything is disabled except the eye, which is the way out.
    const locked = busy || category.hidden;

    return (
      <Col md={4} key={category.id} className="mb-2">
        <div className="d-flex align-items-center">
          <Button
            color="link"
            size="sm"
            className="p-0 mr-2"
            title={category.hidden ? "Unhide to edit" : "Set emoji"}
            disabled={locked}
            onClick={() => setEmojiFor(category)}
          >
            {category.emoji || "🏷️"}
          </Button>

          {editingId === category.id ? (
            <Input
              bsSize="sm"
              autoFocus
              value={draftName}
              style={{ maxWidth: "12rem" }}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={() => saveRename(category)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveRename(category);
                if (e.key === "Escape") setEditingId(null);
              }}
            />
          ) : (
            <>
              <span className={category.hidden ? "text-muted" : undefined}>
                {category.name}
              </span>
              {category.hidden && (
                <Badge color="secondary" className="ml-1">
                  Hidden
                </Badge>
              )}
              <Button
                color="link"
                size="sm"
                className="p-0 ml-2"
                title={category.hidden ? "Unhide to rename" : "Rename"}
                disabled={locked}
                onClick={() => {
                  setEditingId(category.id);
                  setDraftName(category.name);
                }}
              >
                <i className="fas fa-pen" />
              </Button>
            </>
          )}

          <span className="ml-auto d-flex align-items-center">
            <Badge
              color={category.questionCount === 0 ? "warning" : "light"}
              className={`border mr-2${category.hidden ? " text-muted" : ""}`}
              title={`${category.questionCount} questions`}
            >
              {category.questionCount}
            </Badge>

            <Button
              color="link"
              size="sm"
              className="p-0 mr-2"
              title={
                category.hidden
                  ? "Unhide to promote"
                  : category.favorite
                  ? "Un-promote"
                  : "Promote (favorite)"
              }
              disabled={locked}
              onClick={() =>
                patch(category, {
                  categoryId: category.id,
                  favorite: !category.favorite,
                })
              }
            >
              <i
                className={
                  category.favorite ? "fas fa-star text-warning" : "far fa-star"
                }
              />
            </Button>

            <Button
              color="link"
              size="sm"
              className="p-0 mr-2"
              title={category.hidden ? "Unhide" : "Hide from end users"}
              disabled={busy}
              onClick={() =>
                patch(category, {
                  categoryId: category.id,
                  hidden: !category.hidden,
                })
              }
            >
              <i
                className={category.hidden ? "fas fa-eye-slash" : "fas fa-eye"}
              />
            </Button>
          </span>
        </div>
      </Col>
    );
  };

  return (
    <div>
      <MenuBar />
      <div className="container-fluid mt-3 pb-5">
        <ContentSectionTabs active="categories" />

        <Card className="mb-3 bg-light">
          <CardBody className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="mb-1">Categories</h1>
              <div className="text-muted">
                {categories.length} declared · {inUseCount} in use ·{" "}
                {promoted.length} promoted
              </div>
            </div>
            <div>
              <Button
                color="primary"
                outline
                onClick={() => setShowCreate(!showCreate)}
              >
                <i className="fas fa-plus mr-1" />
                New category
              </Button>
            </div>
          </CardBody>
        </Card>

        {showCreate && (
          <Card className="mb-3">
            <CardBody>
              <Row form>
                <Col md={5}>
                  <Input
                    placeholder="Group (e.g. Science)"
                    value={newGroup}
                    onChange={(e) => setNewGroup(e.target.value)}
                  />
                </Col>
                <Col md={5}>
                  <Input
                    placeholder="Name (e.g. Space)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Button color="primary" block onClick={createCategory}>
                    Create
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {candidates.length > 0 && (
          <Alert color="warning">
            <strong>Possible duplicates.</strong> These names normalise to the
            same value — merging moves every question and retires the other
            name.
            <ul className="mb-0 mt-2">
              {candidates.map((candidate) => (
                <li key={candidate.normalized}>
                  {candidate.categories
                    .map((category) => `${category.group}:${category.name}`)
                    .join(" ↔ ")}
                  {candidate.categories
                    .filter(
                      (category) =>
                        category.id !== candidate.suggestedTarget.id,
                    )
                    .map((category) => (
                      <Button
                        key={category.id}
                        color="link"
                        size="sm"
                        className="p-0 ml-2"
                        title="Merge"
                        onClick={() =>
                          setPendingMerge({
                            sourceId: category.id,
                            targetId: candidate.suggestedTarget.id,
                            label: `${candidate.suggestedTarget.group}:${candidate.suggestedTarget.name}`,
                          })
                        }
                      >
                        <i className="fas fa-code-branch mr-1" />
                        Merge into {candidate.suggestedTarget.name}
                      </Button>
                    ))}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Card className="mb-3 border-warning">
          <CardHeader className="d-flex justify-content-between align-items-center bg-white">
            <span>
              <i className="fas fa-star text-warning mr-2" />
              Promoted categories —{" "}
              <span className="text-muted">the “Popular” order users see</span>
            </span>
            <small className="text-muted">{promoted.length} promoted</small>
          </CardHeader>
          <CardBody>
            {promoted.length === 0 ? (
              <span className="text-muted">
                Nothing promoted yet — star a category below to add it here.
              </span>
            ) : (
              promoted.map((category, index) => (
                <div
                  key={category.id}
                  className="d-flex align-items-center py-1 border-bottom"
                >
                  <Badge color="warning" className="mr-2">
                    {index + 1}
                  </Badge>
                  <ButtonGroup size="sm" className="mr-3">
                    <Button
                      color="secondary"
                      outline
                      title="Move up"
                      disabled={index === 0 || busyId === category.id}
                      onClick={() =>
                        patch(category, {
                          categoryId: category.id,
                          favoriteRank: index,
                        })
                      }
                    >
                      <i className="fas fa-arrow-up" />
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      title="Move down"
                      disabled={
                        index === promoted.length - 1 || busyId === category.id
                      }
                      onClick={() =>
                        patch(category, {
                          categoryId: category.id,
                          favoriteRank: index + 2,
                        })
                      }
                    >
                      <i className="fas fa-arrow-down" />
                    </Button>
                  </ButtonGroup>
                  {category.emoji && (
                    <span className="mr-2">{category.emoji}</span>
                  )}
                  <strong>{category.group}</strong>
                  <span className="text-muted"> : {category.name}</span>
                  <span className="ml-auto d-flex align-items-center">
                    <Badge color="light" className="border mr-2">
                      {category.questionCount}
                    </Badge>
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      title="Remove from promoted"
                      disabled={busyId === category.id}
                      onClick={() =>
                        patch(category, {
                          categoryId: category.id,
                          favorite: false,
                        })
                      }
                    >
                      <i className="fas fa-star text-warning" />
                    </Button>
                  </span>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        {loading ? (
          <Loader light />
        ) : (
          <Card>
            <CardBody>
              {groups.map((group) => (
                <div key={group.group} className="mb-4">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-2">
                    <h4 className="mb-1">{group.group}</h4>
                    <small className="text-muted">
                      {group.questionCount} questions
                    </small>
                  </div>
                  <Row>{group.categories.map(renderCategory)}</Row>
                </div>
              ))}
            </CardBody>
          </Card>
        )}
      </div>

      {emojiFor && (
        <EmojiModal
          category={emojiFor}
          isOpen
          saving={busyId === emojiFor.id}
          onClose={() => setEmojiFor(null)}
          onSave={async (emoji) => {
            const target = emojiFor;
            setEmojiFor(null);
            await patch(target, { categoryId: target.id, emoji });
          }}
        />
      )}
      {pendingMerge && (
        <ConfirmModal
          isOpen
          title="Merge these categories?"
          confirmLabel="Merge"
          onCancel={() => setPendingMerge(null)}
          onConfirm={() => {
            const { sourceId, targetId } = pendingMerge;
            setPendingMerge(null);
            void merge(sourceId, targetId);
          }}
        >
          <p className="mb-0 text-muted">
            Every question moves into <strong>{pendingMerge.label}</strong> and
            the other category is retired. This screen cannot undo it.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};
